<template>
  <section class="snomed-what">
    <div class="snomed-container">
      <div class="snomed-title-row">
        <svg class="snomed-search-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle cx="13" cy="13" r="9" stroke="white" stroke-width="2.5" />
          <line x1="20" y1="20" x2="30" y2="30" stroke="white" stroke-width="2.5" stroke-linecap="round" />
        </svg>
        <h2 class="snomed-h2">{{ content.title }}</h2>
      </div>

      <div class="snomed-intro">
        <p class="snomed-intro-highlight">{{ content.subtitle }}</p>
        <p class="snomed-intro-body">{{ content.desc1 }}</p>
        <p class="snomed-intro-body">{{ content.desc2 }}</p>
      </div>

      <p class="snomed-section-label">{{ content.featuresTitle }}</p>

      <div class="snomed-cards">
        <article v-for="(card, index) in cards" :key="index" class="snomed-card">
          <p class="snomed-card-title">{{ card.title }}</p>
          <p class="snomed-card-body">{{ card.body }}</p>
        </article>
      </div>

      <div class="snomed-cta-row">
        <button type="button" class="snomed-cta-btn">
          {{ content.learnMore }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();

const messages = {
  "zh-TW": {
    title: "什麼是 LOINC？",
    subtitle:
      "LOINC (Logical Observation Identifiers Names and Codes)",
    desc1: "用於標準化檢驗、觀察與臨床量測的識別碼與名稱體系。",
    desc2: "促進跨系統、跨機構的資料交換與一致性表達。",
    featuresTitle: "LOINC 的特色",
    learnMore: "了解更多",
    cards: [
      {
        title: "標準化檢驗與觀察",
        body: "將檢驗項目、量測與臨床觀察以一致的代碼與命名表達，降低不同系統間的理解落差。",
      },
      {
        title: "支援互通與交換",
        body: "讓不同醫療系統能正確傳遞與解讀檢驗/觀察資料，提升跨院、跨系統的資訊流通效率。",
      },
      {
        title: "可延伸多元應用",
        body: "可應用於臨床決策支援、研究分析與公共衛生，支持資料再利用與進階分析。",
      },
    ],
  },
  "en-US": {
    title: "What is LOINC?",
    subtitle:
      "LOINC (Logical Observation Identifiers Names and Codes)",
    desc1:
      "A universal identifier and naming system for laboratory tests, clinical measurements, and observations.",
    desc2:
      "Enables consistent representation and data exchange across systems and organizations.",
    featuresTitle: "Features of LOINC",
    learnMore: "Learn More",
    cards: [
      {
        title: "Standardized Observations [Pending Confirmation]",
        body: "Encodes tests and observations with consistent identifiers and names, reducing ambiguity between different systems. [Pending Confirmation]",
      },
      {
        title: "Interoperability Support [Pending Confirmation]",
        body: "Helps healthcare systems transmit and interpret observation data accurately, improving cross-organization data exchange. [Pending Confirmation]",
      },
      {
        title: "Extensible Use Cases [Pending Confirmation]",
        body: "Applicable to CDS, research, and public health to support reuse and advanced analytics. [Pending Confirmation]",
      },
    ],
  },
};

const content = computed(() => messages[locale.value] || messages["zh-TW"]);
const cards = computed(() => content.value.cards);
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.snomed-what {
  font-family: "Noto Sans TC", sans-serif;
  background: #022964;
  padding: 64px 80px 72px;
}

.snomed-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.snomed-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
}

.snomed-search-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.snomed-h2 {
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.25;
  margin: 0;
}

.snomed-intro {
  margin-bottom: 32px;
}

.snomed-intro-highlight {
  font-size: 20px;
  font-weight: 700;
  color: #fff9b9;
  margin: 0 0 8px;
  line-height: 1.6;
}

.snomed-intro-body {
  font-size: 20px;
  font-weight: 400;
  color: #ffffff;
  margin: 0;
  line-height: 1.8;
}

.snomed-intro-body.indent {
  padding-left: 1em;
}

.snomed-section-label {
  font-size: 20px;
  font-weight: 700;
  color: #fff9b9;
  margin: 0 0 20px;
}

.snomed-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 40px;
}

.snomed-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.snomed-card-title {
  font-size: 20px;
  font-weight: 700;
  color: #0098da;
  line-height: 1.5;
  margin: 0;
}

.snomed-card-body {
  font-size: 20px;
  font-weight: 400;
  color: #022964;
  line-height: 1.8;
  margin: 0;
}

.snomed-cta-row {
  display: flex;
  justify-content: center;
}

.snomed-cta-btn {
  background: #ffffff;
  color: #022964;
  border: none;
  border-radius: 100px;
  padding: 8px 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.snomed-cta-btn:hover {
  background: #00a0e9;
  color: #ffffff;
}

@media (max-width: 960px) {
  .snomed-what {
    padding: 56px 40px 64px;
  }

  .snomed-cards {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .snomed-what {
    padding: 48px 24px 56px;
  }

  .snomed-h2 {
    font-size: 24px;
  }

  .snomed-search-icon {
    width: 24px;
    height: 24px;
  }

  .snomed-title-row {
    gap: 10px;
    margin-bottom: 22px;
  }

  .snomed-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .snomed-card {
    padding: 24px 20px;
    gap: 18px;
  }

  .snomed-cta-btn {
    padding: 11px 40px;
    font-size: 14px;
  }
}

@media (max-width: 400px) {
  .snomed-what {
    padding: 40px 20px 48px;
  }
}
</style>
